import { CAT_CR_REP_BASE, CAT_ED_REP_BASE, REPORT_TYPE } from '@core/constants/REPORTS';
import { generateCode } from './uuid';
import { ROUTES } from '@core/constants/ROUTES';
import { SEARCHER_OPTIONS, SEARCHER_TYPES } from '@core/constants/SEARCHER';
import { SWITCH_AI_DEFAULT } from '@core/constants/DEFAULT';
import { addStoreFiltersState } from './store';
import { SUB_STRING_CODE } from '@core/constants/CONSTANTS';
import { MAPPED_VIDEO_ED_TYPE } from '@core/constants/FILTERS';

/**
 * Common code to go to the report list view.
 * @param {string} [reportType] - Report type to go to the report list view. Default: REPORT_TYPE.ED_MOST_VIEWED.
 * @returns {string} - URL to go to the report list view.
 */
export const getReportListView = (reportType = REPORT_TYPE.ED_MOST_VIEWED) => {
    /** @type {URLSearchParams} */
    const urlParams = new URLSearchParams(window.location.search);
    /** @type {string} */
    const searcherIdCode = generateCode();
    /** @type {string} */
    let url = `${ROUTES.REPORTS_LIST}?${SEARCHER_TYPES.REPORT_TYPE}=${
        reportType || REPORT_TYPE.ED_MOST_VIEWED
    }&${SEARCHER_TYPES.SEARCHER_ID}=${searcherIdCode}`;

    if (reportType.includes(REPORT_TYPE.CR_LAST_VIDEOS)) {
        url += `&${SEARCHER_TYPES.OPTION}=${SEARCHER_OPTIONS.CR_VIDEOS}`;
    } else if (reportType.includes(REPORT_TYPE.ED_LAST_VIDEOS)) {
        url += `&${SEARCHER_TYPES.OPTION}=${SEARCHER_OPTIONS.ED_VIDEOS}`;
    } else {
        if (reportType.includes(CAT_CR_REP_BASE)) {
            url += `&${SEARCHER_TYPES.OPTION}=${SEARCHER_OPTIONS.CR_IMGS}`;
        } else {
            url += `&${SEARCHER_TYPES.OPTION}=${SEARCHER_OPTIONS.ED_IMGS}`;
        }
    }

    url += `&${SEARCHER_TYPES.AI}=${
        urlParams.get(SEARCHER_TYPES.AI) || SWITCH_AI_DEFAULT
    }`;

    addStoreFiltersState(searcherIdCode);

    return url;
};

/**
 * Get the type from the section.
 * @param {string} section - Section to get the type.
 * @returns {string} - The type from the section.
 */
export const getTypeFromSection = (section) => {
    /** @type {string} */
    const result = section.includes('/') ? section.split('/')[1] : section;

    return result.replace(/\s/g, '');
};

/**
 * Get the current report type.
 * @returns {string} - The current report type.
 */
export const getCurrentReportType = () => {
    /** @type {URLSearchParams} */
    const urlParams = new URLSearchParams(window.location.search);

    return urlParams.has(SEARCHER_TYPES.REPORT_TYPE)
        ? `${
              SEARCHER_TYPES.OPTION === SEARCHER_OPTIONS.CR_IMGS
                  ? CAT_CR_REP_BASE
                  : CAT_ED_REP_BASE
          }${urlParams.get(SEARCHER_TYPES.REPORT_TYPE) || 'LATEST'}`
        : REPORT_TYPE.ED_MOST_VIEWED;
};

/**
 * Remove the base category type.
 * @param {string} section - Section to remove the base category type.
 * @returns {string} - The section without the base category type.
 */
export const removeBaseCatType = (section) => {
    return section.replace(/cat.*?(Ed|Cr).*?Rep/g, '');
};

/**
 * Get the last report type.
 * @param {Array<string>} searchArray - Array with the search filters.
 * @returns {string} - The last report type.
 */
export const getLastReportType = (searchArray) => {
    return searchArray.filter((filter) => filter.includes(SUB_STRING_CODE.CAT))[0];
};

/**
 * Check if the type of the report is edited videos
 * @param {string} typeValue - The type of the report.
 * @returns {boolean}
 */
export const isTypeEditedVideos = (typeValue) => {
    return Object.values(MAPPED_VIDEO_ED_TYPE).includes(typeValue);
};

/**
 * Check if the type of the section is results view
 * @param {string} typeValue - The type of the section.
 * @returns {boolean}
 */
export const isTypeReports = (typeValue) => {
    return Object.values(REPORT_TYPE).includes(typeValue);
};

/**
 * Check if the user is in the reports video list view.
 * @returns {boolean}
 */
export const isReportVideosList = () => {
    const /** @type {URLSearchParams} */ urlParams = new URLSearchParams(
            window.location.search,
        );

    return (
        window.location.pathname.includes(ROUTES.REPORTS_LIST) &&
        urlParams.has(SEARCHER_TYPES.REPORT_TYPE) &&
        urlParams.get(SEARCHER_TYPES.REPORT_TYPE).includes(REPORT_TYPE.ED_LAST_VIDEOS)
    );
};

/**
 * Check if the view is the detail lightbox view.
 * @returns {boolean} - The view is the detail lightbox view.
 */
export const isDetailLightboxView = () => {
    const /** @type {URLSearchParams} */ urlParams = new URLSearchParams(
            window.location.search,
        );

    return (
        window.location.pathname === ROUTES.REPORT_DETAILS &&
        urlParams.has(SEARCHER_TYPES.LIGHTBOX_ID)
    );
};

/**
 * Check if the view is the details view.
 * @returns {boolean} - The view is the details view.
 */
export const isReportDetailsView = () => {
    return window.location.pathname === ROUTES.REPORT_DETAILS && !isDetailLightboxView();
};

/**
 * Check if the view is the lightbox view.
 * @returns {boolean} - The view is the lightbox view.
 */
export const isLightboxView = () => {
    return window.location.pathname.includes(ROUTES.LIGHT_BOX);
};

/**
 * Check if the view is the report list view.
 * @returns {boolean} - The view is the report list view.
 */
export const isReportListView = () => {
    return window.location.pathname.includes(ROUTES.REPORTS_LIST);
};

/**
 * Check if the view is Creative Report type.
 * @returns {boolean} - The view is Creative Report type.
 */
export const isCreativeReportType = () => {
    const /** @type {URLSearchParams} */ urlParams = new URLSearchParams(
            window.location.search,
        );

    return (
        urlParams.has(SEARCHER_TYPES.REPORT_TYPE) &&
        urlParams
            .get(SEARCHER_TYPES.REPORT_TYPE)
            .toLowerCase()
            .includes(SEARCHER_OPTIONS.CR_CODE)
    );
};
